import React, { useState, useEffect } from 'react';
import { getPermissoesForCnpj, savePermissoesForCnpj, MODULOS } from '../../utils/permissions';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from './DefaultModal';

const acoes = [
  { key: 'abrir', label: 'Abrir' },
  { key: 'criar', label: 'Criar' },
  { key: 'editar', label: 'Editar' },
  { key: 'excluir', label: 'Excluir' },
  { key: 'exportar', label: 'Exportar' },
];

export default function ManageAccessModal({ show, onClose, onSaveSuccess, cpf, userName, currentCnpj, associacaoName }) {
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (show && cpf && currentCnpj) {
      carregarPermissoes();
    }
  }, [show, cpf, currentCnpj]);

  const carregarPermissoes = async () => {
    setLoading(true);
    try {
      const permissoes = await getPermissoesForCnpj(cpf, currentCnpj);
      console.log('📥 Permissões carregadas no modal:', permissoes);
      setPerms(permissoes || {});
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const togglePermissao = (modulo, acao) => {
    setPerms(prev => ({
      ...prev,
      [modulo]: {
        ...(prev[modulo] || { abrir: true, criar: true, editar: true, excluir: true, exportar: true }),
        [acao]: !(prev[modulo]?.[acao] ?? true),
      },
    }));
  };

  const toggleModuloCompleto = (modulo) => {
    const permissoesAtuais = perms[modulo] || {};
    const todasHabilitadas = acoes.every(a => permissoesAtuais[a.key] !== false);
    
    setPerms(prev => ({
      ...prev,
      [modulo]: {
        abrir: !todasHabilitadas,
        criar: !todasHabilitadas,
        editar: !todasHabilitadas,
        excluir: !todasHabilitadas,
        exportar: !todasHabilitadas,
      },
    }));
  };

  const salvar = async () => {
    setSaving(true);
    try {
      console.log('🔵 Iniciando salvamento de permissões...', {
        cpf,
        currentCnpj,
        perms
      });
      
      const resultado = await savePermissoesForCnpj(cpf, currentCnpj, perms);
      
      if (resultado) {
        console.log('✅ Permissões salvas com sucesso!');
        onClose();
        if (onSaveSuccess) {
          onSaveSuccess();
        }
      } else {
        console.error('❌ Falha ao salvar permissões');
        showDefaultModal('error', 'Erro ao salvar permissões. Verifique o console para mais detalhes.');
      }
    } catch (error) {
      console.error('❌ Erro ao salvar permissões:', error);
      showDefaultModal('error', 'Erro ao salvar permissões: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setSaving(false);
    }
  };

  // Ordenar módulos alfabeticamente pelo label
  const modulosOrdenados = [...MODULOS].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div 
      className="modal show d-block" 
      tabIndex="-1" 
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1055,
        position: 'fixed',
        inset: 0,
      }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div 
            className="modal-header bg-success text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title mb-0">
              <i className="bi bi-shield-lock me-2"></i>
              GERENCIAR PERMISSÕES
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose} 
              disabled={saving}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label mb-1 fw-bold">Associação:</label>
                <input 
                  type="text" 
                  className="form-control form-control-sm" 
                  value={associacaoName || currentCnpj}
                  disabled
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <label className="form-label mb-1 fw-bold">Usuário:</label>
                <input 
                  type="text" 
                  className="form-control form-control-sm" 
                  value={userName || 'Não informado'}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive-sm shadow-sm mb-0 small" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <table className="table table-hover table-striped table-sm mb-0">
                  <thead className="table-secondary sticky-top" style={{ top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '200px' }}>Módulo</th>
                      {acoes.map(a => (
                        <th key={a.key} className="text-center" style={{ width: '90px' }}>
                          {a.label}
                        </th>
                      ))}
                      <th className="text-center" style={{ width: '80px' }}>Todos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modulosOrdenados.map(modulo => {
                      const permissoesModulo = perms[modulo.key] || {};
                      const todasHabilitadas = acoes.every(a => permissoesModulo[a.key] !== false);
                      
                      return (
                        <tr key={modulo.key}>
                          <td><strong>{modulo.label}</strong></td>
                          {acoes.map(acao => (
                            <td key={acao.key} className="text-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={permissoesModulo[acao.key] !== false}
                                onChange={() => togglePermissao(modulo.key, acao.key)}
                                disabled={saving}
                              />
                            </td>
                          ))}
                          <td className="text-center">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={todasHabilitadas}
                              onChange={() => toggleModuloCompleto(modulo.key)}
                              disabled={saving}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button 
              type="button"
              className="btn btn-secondary btn-sm" 
              onClick={onClose} 
              disabled={saving}
            >
              <i className="bi bi-x-lg me-1"></i>
              Cancelar
            </button>
            <button 
              type="button"
              className="btn btn-success btn-sm" 
              onClick={salvar} 
              disabled={loading || saving}
            >
              <i className="bi bi-check-lg me-1"></i>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </div>
  );
}

